import { useEffect, useState } from "react";
import { useTrans } from "./useTrans";
import { useAuthData } from "../contexts";
import { NavItem } from "../containers/AppNav/AppNav";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom, NewThreadMessagesAtom } from "../atoms";
import { APPC, cPath, UTIL } from "../config";

export const useNavItems = (type: string = "main"): NavItem[] => {
    const { t } = useTrans();
    const { user } = useAuthData();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const newThreadMessagesAtom = useRecoilValue(NewThreadMessagesAtom);

    const newThreadCounts = newThreadMessagesAtom?.length;

    const [mainNavItems, setMainNavItems] = useState<NavItem[]>([]);

    const CATEGORY = APPC.BE.Category;
    const UT = APPC.BE.User.USERTYPE;

    const userNavMap: { [key: string]: string[] } = {
        [UT.USERTYPE_SUPER_ADMIN]: [
            "dashboard",
            "administration",
            "school",
            "company",
            "user",
            "coordinator-request",
            "category",
            "region-department",
            "training",
            "typology",
            "skill",
            "gesture",
            "timesheet-type",
        ],
        [UT.USERTYPE_INTERNSHIP_COORDINATOR_TRAINER]: [
            "dashboard",
            "my-internship",
            "find-internship-school",
            "partnership-company",
            "partnership-company-request",
            "administration",
            "my-school",
            "user",
            "school-batch",
            "messages",
        ],
        [UT.USERTYPE_INTERNSHIP_REFERENCE_TRAINER]: [
            "dashboard",
            "my-internship",
            "messages",
        ],
        [UT.USERTYPE_PEDAGOGICAL_REFERENCE_TRAINER]: [
            "dashboard",
            "my-internship",
            "messages",
        ],
        [UT.USERTYPE_STUDENT]: ["dashboard", "my-internship", "messages"],
        [UT.USERTYPE_INTERNSHIP_COORDINATOR]: [
            "dashboard",
            "internship",
            "find-internship-company",
            "partnership-school",
            "partnership-school-request",
            "administration",
            "my-company",
            "user",
            "messages",
        ],
        [UT.USERTYPE_INTERNSHIP_SUPERVISOR]: [
            "dashboard",
            "internship",
            "messages",
        ],
        [UT.USERTYPE_INTERNSHIP_TUTOR]: ["dashboard", "internship", "messages"],
    };

    const items: NavItem[] = [
        {
            key: "dashboard",
            label: t("nav:dashboard"),
            url: cPath("ADMIN.DASHBOARD_PAGE"),
            iconName: "icl-view-quilt",
            divider: true,
        },
        {
            key: "my-internship",
            label: t("nav:internship"),
            url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
            iconName: "icl-book",
        },
        {
            key: "find-internship-school",
            label: t("nav:FindInternship"),
            url: cPath("ADMIN.FIND_INTERNSHIP_SCHOOL_PAGE"),
            iconName: "icl-find-in-page",
        },
        {
            key: "partnership-company",
            label: t("nav:partnership"),
            url: cPath("ADMIN.PARTNERSHIP_COMPANY_PAGE"),
            iconName: "icl-handshake",
        },
        // {
        //     key: "partnership-company-request",
        //     label: t("nav:partnershipRequest"),
        //     url: cPath("ADMIN.REQUEST_PARTNERSHIP_COMPANY_PAGE"),
        //     iconName: "icl-queue-add",
        // },
        {
            key: "internship",
            label: t("nav:internship"),
            url: cPath("ADMIN.INTERNSHIP_PAGE"),
            iconName: "icl-book",
        },
        {
            key: "find-internship-company",
            label: t("nav:FindInternship"),
            url: cPath("ADMIN.FIND_INTERNSHIP_COMPANY_PAGE"),
            iconName: "icl-find-in-page",
        },
        {
            key: "partnership-school",
            label: t("nav:partnership"),
            url: cPath("ADMIN.PARTNERSHIP_SCHOOL_PAGE"),
            iconName: "icl-handshake",
        },
        // {
        //     key: "partnership-school-request",
        //     label: t("nav:partnershipRequest"),
        //     url: cPath("ADMIN.REQUEST_PARTNERSHIP_SCHOOL_PAGE"),
        //     iconName: "icl-queue-add",
        // },
        {
            key: "administration",
            label: t("nav:administration"),
        },
        {
            key: "my-school",
            label: t("nav:mySchool"),
            url: cPath("ADMIN.MY_SCHOOL_PAGE"),
            iconName: "icl-school",
        },
        {
            key: "my-company",
            label: t("nav:myCompany"),
            url: cPath("ADMIN.MY_COMPANY_PAGE"),
            iconName: "icl-building-business",
        },
        {
            key: "school-batch",
            label: t("nav:schoolBatch"),
            url: cPath("ADMIN.SCHOOL_BATCH_PAGE"),
            iconName: "icl-flag-alt",
        },
        {
            key: "school",
            label: t("nav:school"),
            url: cPath("ADMIN.SCHOOL_PAGE"),
            iconName: "icl-school",
        },
        {
            key: "company",
            label: t("nav:company"),
            url: cPath("ADMIN.COMPANY_PAGE"),
            iconName: "icl-building-business",
        },
        {
            key: "user",
            label: t("nav:user"),
            url: cPath("ADMIN.USER_PAGE"),
            iconName: "icl-id-badge",
        },
        {
            key: "coordinator-request",
            label: t("nav:coordinatorRequest"),
            url: cPath("ADMIN.COORDINATOR_REQUEST_PAGE"),
            iconName: "icl-user-plus",
            divider: true,
        },
        {
            key: "category",
            label: t("nav:category"),
            url: cPath("ADMIN.CATEGORY_PAGE"),
            iconName: "icl-widgets",
        },
        {
            key: "region-department",
            label: t("nav:regionAndDepartment"),
            url: cPath("ADMIN.REGION_PAGE"),
            iconName: "icl-globe-alt",
        },
        {
            key: "training",
            label: t("nav:training"),
            url: cPath("ADMIN.TRAINING_PAGE"),
            iconName: "icl-loop-square",
        },
        {
            key: "typology",
            label: t("nav:typology"),
            url: cPath("ADMIN.TYPOLOGY_PAGE"),
            iconName: "icl-call-split",
        },
        {
            key: "skill",
            label: t("nav:skill"),
            url: cPath("ADMIN.SKILL_PAGE"),
            iconName: "icl-lightbulb",
        },
        {
            key: "gesture",
            label: t("nav:gesture"),
            url: cPath("ADMIN.GESTURE_PAGE"),
            iconName: "icl-hand-lizard",
        },
        {
            key: "timesheet-type",
            label: t("nav:timesheetType"),
            url: cPath("ADMIN.TIMESHEET_TYPE_PAGE"),
            iconName: "icl-spreadsheet",
        },
        {
            key: "messages",
            label: t("nav:messages"),
            url: cPath("ADMIN.MESSAGES_PAGE"),
            iconName: "icl-chat-bubble",
        },
    ];

    useEffect(() => {
        if (
            UTIL.isSchoolCompanyMultiSelect(user?.userType) &&
            !activeSchoolOrCompany
        ) {
            return;
        }

        let selectedUserType = user?.userType as string;

        if (selectedUserType === UT.USERTYPE_SCHOOL_DIRECTOR) {
            selectedUserType = UT.USERTYPE_INTERNSHIP_COORDINATOR_TRAINER;
        } else if (selectedUserType === UT.USERTYPE_COMPANY_DIRECTOR) {
            selectedUserType = UT.USERTYPE_INTERNSHIP_COORDINATOR;
        } else if (selectedUserType === UT.USERTYPE_SCHOOL_AND_COMPANY) {
            if (
                activeSchoolOrCompany &&
                activeSchoolOrCompany?.["@type"] === CATEGORY.TYPE.TYPE_SCHOOL
            ) {
                selectedUserType = UT.USERTYPE_INTERNSHIP_COORDINATOR_TRAINER;
            } else if (
                activeSchoolOrCompany &&
                activeSchoolOrCompany?.["@type"] === CATEGORY.TYPE.TYPE_COMPANY
            ) {
                selectedUserType = UT.USERTYPE_INTERNSHIP_COORDINATOR;
            }
        }

        const selNav = items.filter((i) => {
            if (newThreadCounts > 0 && i.key === "messages") {
                i.count = newThreadCounts;
            }
            return userNavMap[selectedUserType]?.includes(i.key) ? i : false;
        });

        setMainNavItems([...selNav]);
    }, [activeSchoolOrCompany, newThreadMessagesAtom]);

    const userNavItems: NavItem[] = [
        {
            key: "user-profile",
            label: t("nav:profile"),
            url: cPath("ADMIN.PROFILE_PAGE"),
            iconName: "icl-address-card",
        },
        {
            key: "user-logout",
            label: t("nav:logout"),
            url: cPath("ADMIN.LOGOUT"),
            iconName: "icl-account-logout",
        },
    ];

    return type === "user" ? userNavItems : mainNavItems;
};
